import React, { memo, useRef, useReducer } from 'react';
import { View, FlatList, Clipboard } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Space,
  IconAdd,
  LoadingContainer,
  WebScrollView,
  IconTelephone,
} from '@redlink/components/atoms';
import {
  PreviewContainer,
  useAlert,
  useDialog,
} from '@redlink/components/molecules';
import {
  ProductEditPage,
  ShopContactEditPage,
} from '@redlink/components/pages';
import {
  CategoryList,
  ProductItem,
  Pagination,
} from '@redlink/components/muscles';
import { ListContainer } from '@redlink/components/organisms';
import { PreviewShopPage, CategoryEditPage } from '@redlink/components/pages';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import {
  useQuery,
  useMutation,
  getPageCategoriesQuery,
  getPageProfileQuery,
  getPageProductsQuery,
  setLinkVisibilityQuery,
  removeLinkQuery,
  cache,
  getShopContactsPreviewQuery,
} from '@redlink/services';
import { freshCacheForVisibility } from './cache';
import { initialState, reducer, types } from './store';

const PAGE_SIZE = 10;

export const ShopPage = memo(() => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  const { staticData } = useValues();
  const alert = useAlert();
  const { showConfirmDialog } = useDialog();
  const previewShopPageRef = useRef(null);

  const [
    {
      categoriesIsVisible,
      newProductIsVisible,
      currentProduct,
      selectedCategory,
      fetchMoreIsBusy,
      currentPageIndex,
      newShopContactVisible,
    },
    dispatch,
  ] = useReducer(reducer, initialState);

  const refetchAllQuery = () => {
    fetchMoreProducts(currentPageIndex, selectedCategory);
    previewShopPageRef.current && previewShopPageRef.current.refetch();
  };

  const [setLinkVisibility, { loading: setVisibiltyIsBusy }] = useMutation(
    setLinkVisibilityQuery,
    {
      done: () =>
        previewShopPageRef.current && previewShopPageRef.current.refetch(),
      update: freshCacheForVisibility({
        category: selectedCategory,
        pageIndex: currentPageIndex,
        pageSize: PAGE_SIZE,
      }),
    }
  );

  const [removeLink] = useMutation(removeLinkQuery, {
    done: refetchAllQuery,
  });

  const renderItem = ({ item, index }) => {
    return (
      <ProductItem
        {...{ item }}
        onEditPressed={() => {
          dispatch({
            type: types.SET_MULTI,
            payload: { currentProduct: item, newProductIsVisible: true },
          });
        }}
        onCopyPressed={() => {
          Clipboard.setString(item.shortLinkUrl);
          alert.show(dictionary.copiedInClipboard, { type: 'info' });
        }}
        onChangeAvailability={(value) => {
          setLinkVisibility({ id: item.id, isVisible: value });
        }}
        onDelete={() => {
          showConfirmDialog({
            message: dictionary.deleteConfirmMessage,
            onConfirm: () => removeLink({ id: item.id }),
          });
        }}
      />
    );
  };

  const {
    data: { PageProfile: { pageName } = {} },
  } = useQuery(getPageProfileQuery);

  const {
    data: { Categories = [] },
  } = useQuery(getPageCategoriesQuery, {
    variables: { type: staticData.categoryType.product },
  });

  const {
    fetchMore,
    loading: getPageProductsIsBusy,
    data: { Products: { totalCount = 0, items: Products = [] } = {} },
  } = useQuery(getPageProductsQuery, {
    variables: {
      skip: 0,
      take: PAGE_SIZE,
    },
  });

  const fetchMoreProducts = (pageIndex, category) => {
    dispatch({
      type: types.SET_FETCH_MORE_IS_BUSY,
      payload: true,
    });
    fetchMore({
      variables: {
        ...(category && { category: category?.item?.id }),
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      },
      updateQuery: (prev, { fetchMoreResult, variables }) => {
        dispatch({
          type: types.SET_FETCH_MORE_IS_BUSY,
          payload: false,
        });

        if (!fetchMoreResult) return prev;

        const newResult = {
          ...prev,
          Products: {
            ...prev.Products,
            totalCount: prev.Products.totalCount,
            items: [...fetchMoreResult.Products.items],
          },
        };

        cache.writeQuery({
          query: getPageProductsQuery,
          data: newResult,
          variables,
        });

        return newResult;
      },
    });
  };

  const {
    loading: getShopContactsPreviewIsBusy,
    data: { ShopContactsPreview: shopContacts },
  } = useQuery(getShopContactsPreviewQuery, {
    variables: { pageName },
  });

  return (
    <View style={styles.container}>
      <WebScrollView>
        <ListContainer
          title={dictionary.categories}
          containerContainerStyle={styles.categoriesContainerStyle}
        >
          <CategoryList
            data={Categories}
            selectedIndex={selectedCategory?.index}
            contentContainerStyle={styles.contentContainerStyle}
            onChange={(item, index) => {
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  currentPageIndex: 0,
                  selectedCategory: { item, index },
                },
              });
              fetchMoreProducts(0, { item, index });
            }}
            onManagePress={() =>
              dispatch({ type: types.SET_CATEGORIES_IS_VISIBLE, payload: true })
            }
          />
        </ListContainer>
        <Space size={25} />
        <ListContainer
          data={Products}
          isFetching={getPageProductsIsBusy}
          title={dictionary.products}
          placeholder={{
            imageSource: require('@redlink/assets/images/landing_image_3.png'),
            description: dictionary.addYourFirstProduct,
          }}
          actions={[
            {
              title: dictionary.addNewProduct,
              variant: 'primary',
              icon: <IconAdd color={colors.white} />,
              onPress: () => {
                dispatch({
                  type: types.SET_MULTI,
                  payload: {
                    currentProduct: null,
                    newProductIsVisible: true,
                  },
                });
              },
            },
            {
              title: dictionary.addShopContact,
              variant: 'greenGlass',
              icon: <IconTelephone color={colors.white} size={20} />,
              onPress: () => {
                dispatch({
                  type: types.SET_SHOP_CONTACTS_IS_VISIBLE,
                  payload: true,
                });
              },
            },
          ]}
        >
          <LoadingContainer
            isBusy={
              fetchMoreIsBusy ||
              setVisibiltyIsBusy ||
              getShopContactsPreviewIsBusy
            }
            hasOverlay={false}
          >
            <FlatList
              contentContainerStyle={styles.contentContainerStyle}
              data={Products}
              ItemSeparatorComponent={() => <Space />}
              {...{ renderItem }}
              showsVerticalScrollIndicator={false}
            />
          </LoadingContainer>
        </ListContainer>
        {totalCount > PAGE_SIZE && (
          <Pagination
            style={styles.paginationContainer}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            currentPage={currentPageIndex}
            onChange={(pageIndex) => {
              dispatch({
                type: types.SET_CURRENT_PAGE_INDEX,
                payload: pageIndex,
              });
              fetchMoreProducts(pageIndex, selectedCategory);
            }}
          />
        )}
      </WebScrollView>
      <PreviewContainer>
        <PreviewShopPage {...{ pageName }} ref={previewShopPageRef} />
      </PreviewContainer>
      <CategoryEditPage
        type={staticData.categoryType.product}
        {...{ pageName }}
        isVisible={categoriesIsVisible}
        onDismiss={() => {
          dispatch({
            type: types.SET_CATEGORIES_IS_VISIBLE,
            payload: false,
          });
        }}
      />
      <ProductEditPage
        {...{ pageName }}
        onSubmit={(isEditMode) => {
          refetchAllQuery();
          dispatch({
            type: types.SET_NEW_PRODUCT_IS_VISIBLE,
            payload: false,
          });
        }}
        isVisible={newProductIsVisible}
        onDismiss={() => {
          dispatch({
            type: types.SET_NEW_PRODUCT_IS_VISIBLE,
            payload: false,
          });
        }}
        {...(currentProduct && {
          data: currentProduct,
        })}
      />
      <ShopContactEditPage
        {...{ pageName }}
        isVisible={newShopContactVisible}
        data={shopContacts}
        onDismiss={() => {
          dispatch({
            type: types.SET_SHOP_CONTACTS_IS_VISIBLE,
            payload: false,
          });
        }}
      />
    </View>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  contentContainerStyle: {
    paddingVertical: 8,
  },
  categoriesContainerStyle: {
    marginTop: 16,
  },
  paginationContainer: {
    margin: 50,
  },
}));
